import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import DigitalIDCard from "@/components/DigitalIDCard";
import ExpenseClaimPanel from "@/components/ExpenseClaimPanel";
import ConferenceMemberPanel from "@/components/conference/ConferenceMemberPanel";
import ProfileCompletionGate from "@/components/ProfileCompletionGate";
import HardwareRegisterTab from "@/components/HardwareRegisterTab";
import CitizenCharterTab from "@/components/charter/CitizenCharterTab";
import { downloadIDCardPDF, downloadReceiptPDF } from "@/lib/pdf";
import {
  IdCard, FileText, CreditCard, Calendar, BookOpen, LifeBuoy,
  CheckCircle2, Clock, AlertTriangle, Download, Printer, Camera, Upload, Receipt,
  Search, ExternalLink, FileDown, Landmark, Newspaper, UserCheck, ScrollText,
} from "lucide-react";

const STATUS_MAP = {
  pending_payment: { label: "Pending Payment", color: "bg-yellow-500", icon: Clock },
  pending_review: { label: "Pending Admin Review", color: "bg-blue-500", icon: Clock },
  active: { label: "Active", color: "bg-brand-green", icon: CheckCircle2 },
  suspended: { label: "Suspended", color: "bg-red-500", icon: AlertTriangle },
  rejected: { label: "Rejected", color: "bg-red-500", icon: AlertTriangle },
};

export default function MemberDashboard() {
  const { user, refresh } = useAuth();
  const [payments, setPayments] = useState([]);
  const [events, setEvents] = useState([]);
  const [circulars, setCirculars] = useState([]);
  const [circularSearch, setCircularSearch] = useState("");
  const [circularCat, setCircularCat] = useState("all");

  useEffect(() => {
    api.get("/payments/mine").then(r => setPayments(r.data)).catch(() => {});
    api.get("/events").then(r => setEvents(r.data)).catch(() => {});
    api.get("/circulars").then(r => setCirculars(r.data)).catch(() => {});
  }, []);

  if (!user || typeof user !== "object") return null;

  // Show one-time profile completion gate for members missing required KELTA fields
  // (typically Google-authed users who skipped the 4-step register form).
  // Also show it for admins arriving with ?complete=1 (from the "Complete now" banner).
  const forceComplete = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("complete") === "1";
  const profileIncomplete = (user.role === "member" && (
    !user.district || !user.local_body_name || !user.local_body_type || !user.mobile
  )) || forceComplete;

  // Legacy onboarding gate: members created by a District/State admin must
  // complete the FULL registration form before using the dashboard.
  const LEGACY_REQUIRED = ["mobile", "dob", "address", "employee_id", "date_of_joining"];
  const legacyIncomplete = user.role === "member" && user.created_via === "admin_offline"
    && LEGACY_REQUIRED.some(k => !(user[k] || "").toString().trim());
  if (legacyIncomplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-blue/5 via-background to-brand-orange/5 flex items-center justify-center p-4" data-testid="legacy-onboarding-gate">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-xl">
          <Card className="p-6 sm:p-8 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-brand-blue/10 flex items-center justify-center mb-4">
              <UserCheck className="w-6 h-6 text-brand-blue" />
            </div>
            <div className="text-xs uppercase tracking-widest text-brand-blue font-semibold">Membership onboarding</div>
            <h1 className="text-2xl font-black tracking-tight mt-1">Complete Your KELTA Membership</h1>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
              Welcome, <strong>{user.full_name}</strong>! Your account was created by your association office.
              Please complete the full membership registration form — personal details, photo, service record and
              documents — to finish your onboarding.
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Your membership fee and approval are handled by your District office. Once approved, your Membership
              Certificate, Digital ID Card and credentials email are issued automatically.
            </p>
            <Link to="/register">
              <Button className="mt-6 bg-brand-blue hover:bg-brand-blue/90 text-white px-8 h-11" data-testid="legacy-complete-membership-btn">
                Complete Membership Form
              </Button>
            </Link>
            <div className="mt-4">
              <Link to="/" className="text-xs text-muted-foreground underline underline-offset-2">Back to home</Link>
            </div>
          </Card>
        </motion.div>
      </div>
    );
  }

  if (profileIncomplete) {
    return <ProfileCompletionGate user={user} onDone={refresh} />;
  }

  const status = STATUS_MAP[user.membership_status] || STATUS_MAP.pending_payment;
  const StatusIcon = status.icon;

  // Citizen Charter: approved members + district/state admins (mirrors backend rule)
  const canCharter = user.membership_status === "active" || user.role !== "member";

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-10">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
            <div>
              <div className="text-xs uppercase tracking-widest text-brand-blue font-semibold">My Dashboard</div>
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Welcome, {user.full_name?.split(" ")[0] || "Member"}</h1>
              <div className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${status.color}`} />
                <StatusIcon className="w-3.5 h-3.5" /> {status.label}
              </div>
            </div>
            {user.membership_status === "pending_payment" && (
              <PayButton onPaid={refresh} user={user} />
            )}
            {user.payment_status !== "paid" && user.membership_status !== "pending_payment" && (
              <PayButton onPaid={refresh} user={user} />
            )}
            {user.payment_status === "paid" && (
              <PayButton onPaid={refresh} user={user} label="Renew Membership" />
            )}
          </div>

          {/* Legacy onboarding: members created by a District/State admin must
              complete the full registration form before their profile is final */}
          {user.created_via === "admin_offline" && (() => {
            const LABELS = { mobile: "Mobile", dob: "Date of Birth", address: "Address", employee_id: "Employee ID", date_of_joining: "Date of Joining" };
            const missing = Object.keys(LABELS).filter(k => !(user[k] || "").toString().trim());
            if (!missing.length) return null;
            return (
              <div className="mb-6 rounded-lg border border-brand-blue/50 bg-brand-blue/5 p-4 flex items-start justify-between gap-4 flex-wrap" data-testid="complete-membership-banner">
                <div className="flex items-start gap-3">
                  <UserCheck className="w-5 h-5 mt-0.5 text-brand-blue shrink-0" />
                  <div>
                    <div className="font-semibold text-sm">Complete your KELTA membership</div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Your account was created by your association office. Please fill the full membership form to finish onboarding.
                      Missing: <span className="font-medium text-foreground">{missing.map(k => LABELS[k]).join(", ")}</span>.
                    </p>
                  </div>
                </div>
                <Link to="/register">
                  <Button size="sm" className="bg-brand-blue hover:bg-brand-blue/90 text-white h-9" data-testid="complete-membership-btn">
                    Complete Membership
                  </Button>
                </Link>
              </div>
            );
          })()}

          <Tabs defaultValue="overview">
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="id" data-testid="tab-id">ID Card</TabsTrigger>
              <TabsTrigger value="receipts" data-testid="tab-receipts">Receipts</TabsTrigger>
              {user.payment_status === "paid" && user.membership_status === "active" && (
                <TabsTrigger value="conference" data-testid="tab-conference">State Conference</TabsTrigger>
              )}
              <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
              <TabsTrigger value="circulars" data-testid="tab-circulars">Circulars</TabsTrigger>
              <TabsTrigger value="claims" data-testid="tab-claims">Expense Claims</TabsTrigger>
              <TabsTrigger value="hardware" data-testid="tab-hardware">Hardware Register</TabsTrigger>
              {canCharter && (
                <TabsTrigger value="charter" data-testid="tab-charter">
                  <ScrollText className="w-3.5 h-3.5 mr-1" /> Citizen Charter
                </TabsTrigger>
              )}
              <TabsTrigger value="helpdesk" data-testid="tab-helpdesk">Help Desk</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-6">
              <div className="grid lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2 space-y-5">
                  <Card className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <IdCard className="w-5 h-5 text-brand-blue" />
                      <div className="font-bold">Membership Status</div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Stat k="Membership ID" v={user.membership_id || "— pending —"} />
                      <Stat k="Designation" v={user.designation} />
                      <Stat k="Department" v={user.department} />
                      <Stat k="District" v={user.district} />
                      <Stat k="Local Body" v={`${user.local_body_name} (${user.local_body_type})`} />
                      <Stat k="Valid till" v={(user.expiry_date || "—").slice(0, 10)} />
                    </div>
                    {user.membership_status === "pending_payment" && (
                      <div className="mt-4 p-3 rounded-md bg-yellow-500/10 border border-yellow-500/30 text-sm">
                        Complete your <strong>₹1,300</strong> membership payment to proceed for admin review.
                      </div>
                    )}
                    {user.membership_status === "pending_review" && (
                      <div className="mt-4 p-3 rounded-md bg-blue-500/10 border border-blue-500/30 text-sm">
                        Payment received. An admin will review and activate your membership soon.
                      </div>
                    )}
                  </Card>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <QuickCard icon={FileText} label="My Receipts" value={payments.length} />
                    <QuickCard icon={Calendar} label="Available Events" value={events.length} />
                    <QuickCard icon={BookOpen} label="New Circulars" value={circulars.length} />
                  </div>
                  <PhotoUploadCard user={user} onUpdated={refresh} />
                </div>
                <div>
                  <Card className="p-5">
                    <div className="text-xs uppercase tracking-widest text-brand-blue font-semibold mb-2">Digital ID Preview</div>
                    {user.membership_id ? (
                      <DigitalIDCard member={user} />
                    ) : (
                      <div className="aspect-[1.6/1] rounded-2xl border-2 border-dashed border-border bg-muted/30 flex items-center justify-center text-sm text-muted-foreground p-6 text-center">
                        Your ID card will appear here once your membership is activated by admin.
                      </div>
                    )}
                  </Card>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="id" className="mt-6">
              {user.membership_id ? (
                <div className="grid lg:grid-cols-2 gap-6 items-center">
                  <div className="print-only">
                    <DigitalIDCard member={user} />
                  </div>
                  <div>
                    <h3 className="font-bold text-2xl">Your KELTA Digital ID</h3>
                    <p className="text-muted-foreground mt-2">
                      Show the QR code from this card at any KELTA event or partner office. Scanning it
                      opens a public, tamper-proof verification page.
                    </p>
                    <div className="mt-5 flex gap-3">
                      <Button
                        onClick={async () => {
                          try {
                            toast.loading("Generating A4 PDF…", { id: "idpdf" });
                            await downloadIDCardPDF(user);
                            toast.success("ID card PDF downloaded", { id: "idpdf" });
                          } catch (e) {
                            toast.error("Could not generate PDF", { id: "idpdf" });
                          }
                        }}
                        data-testid="download-id-pdf-btn"
                      >
                        <Download className="w-4 h-4 mr-1.5" /> Download A4 PDF
                      </Button>
                      <Button onClick={() => window.print()} variant="outline" data-testid="print-id-btn"><Printer className="w-4 h-4 mr-1.5" /> Print</Button>
                    </div>
                  </div>
                </div>
              ) : (
                <Card className="p-10 text-center text-muted-foreground">
                  Your ID card is not yet generated. Complete payment and wait for admin approval.
                </Card>
              )}
            </TabsContent>

            <TabsContent value="receipts" className="mt-6">
              <Card>
                <ul className="divide-y divide-border">
                  {payments.length === 0 && (
                    <li className="p-8 text-center text-muted-foreground text-sm">No receipts yet.</li>
                  )}
                  {payments.map(p => (
                    <li key={p.id} className="p-4 flex items-center justify-between gap-3" data-testid={`receipt-${p.id}`}>
                      <div className="min-w-0">
                        <div className="font-semibold capitalize">{p.purpose} payment</div>
                        <div className="text-xs text-muted-foreground font-mono truncate">{p.receipt_no} • {p.transaction_id}</div>
                        <div className="text-xs text-muted-foreground">{new Date(p.date).toLocaleString()}</div>
                      </div>
                      <div className="text-right flex flex-col items-end gap-1.5">
                        <div className="font-mono font-bold">₹{p.amount}</div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-[10px]">{p.method}</Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs"
                            data-testid={`download-receipt-${p.id}`}
                            onClick={async () => {
                              try {
                                toast.loading("Generating receipt PDF…", { id: `rcpt-${p.id}` });
                                await downloadReceiptPDF(p, user);
                                toast.success("Receipt downloaded", { id: `rcpt-${p.id}` });
                              } catch {
                                toast.error("Could not generate receipt", { id: `rcpt-${p.id}` });
                              }
                            }}
                          >
                            <Download className="w-3 h-3 mr-1" /> A4 PDF
                          </Button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>
            </TabsContent>

            <TabsContent value="conference" className="mt-6">
              <ConferenceMemberPanel user={user} />
            </TabsContent>

            <TabsContent value="events" className="mt-6">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {events.map(e => <EventCard key={e.id} ev={e} />)}
              </div>
            </TabsContent>

            <TabsContent value="circulars" className="mt-6">
              <CircularsPanel
                circulars={circulars}
                search={circularSearch}
                setSearch={setCircularSearch}
                cat={circularCat}
                setCat={setCircularCat}
              />
            </TabsContent>

            <TabsContent value="claims" className="mt-6">
              <ExpenseClaimPanel />
            </TabsContent>

            <TabsContent value="hardware" className="mt-6">
              <HardwareRegisterTab />
            </TabsContent>

            {canCharter && (
              <TabsContent value="charter" className="mt-6">
                <CitizenCharterTab user={user} />
              </TabsContent>
            )}

            <TabsContent value="helpdesk" className="mt-6">
              <HelpDeskPanel />
            </TabsContent>
          </Tabs>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</div>
      <div className="font-medium">{v || "—"}</div>
    </div>
  );
}

function QuickCard({ icon: Icon, label, value }) {
  return (
    <Card className="p-4 hover-lift">
      <Icon className="w-5 h-5 text-brand-blue mb-2" />
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-2xl font-black">{value}</div>
    </Card>
  );
}

function PhotoUploadCard({ user, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const fileRef = React.useRef(null);
  const hasPhoto = !!user?.photo_url;

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image must be smaller than 2MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        setBusy(true);
        await api.patch("/profile", { photo_url: ev.target.result });
        toast.success("Profile photo updated");
        onUpdated?.();
      } catch (e) {
        toast.error("Upload failed");
      } finally { setBusy(false); }
    };
    reader.readAsDataURL(file);
  };

  return (
    <Card className="p-5">
      <div className="flex items-start gap-4">
        <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted flex-shrink-0 ring-1 ring-border">
          {hasPhoto ? (
            <img src={user.photo_url} alt={user.full_name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <Camera className="w-7 h-7" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Camera className="w-4 h-4 text-brand-blue" />
            <div className="font-bold">Profile Photo</div>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {hasPhoto
              ? "Replace your photo anytime. Used on your Digital ID card."
              : "You haven't uploaded a photo yet. Add one so it appears on your Digital ID card."}
          </p>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="dash-photo-input"
          />
          <div className="mt-3 flex gap-2">
            <Button
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="bg-brand-blue text-white hover:bg-brand-blue/90"
              data-testid="dash-photo-upload-btn"
            >
              <Upload className="w-4 h-4 mr-1.5" />
              {busy ? "Uploading…" : hasPhoto ? "Change Photo" : "Upload Photo"}
            </Button>
            {hasPhoto && (
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={async () => {
                  try {
                    setBusy(true);
                    await api.patch("/profile", { photo_url: "" });
                    toast.success("Photo removed");
                    onUpdated?.();
                  } finally { setBusy(false); }
                }}
              >
                Remove
              </Button>
            )}
          </div>
          <p className="text-[10px] text-muted-foreground mt-2">JPG/PNG, max 2MB.</p>
        </div>
      </div>
    </Card>
  );
}

function EventCard({ ev }) {
  const register = async () => {
    try { await api.post(`/events/${ev.id}/register`); toast.success("Registered for event"); }
    catch (e) { toast.error("Could not register"); }
  };
  return (
    <Card className="p-5 hover-lift">
      <div className="flex items-center gap-2 mb-2">
        <Badge variant="outline" className="text-[10px]">{ev.type}</Badge>
        <span className="text-xs text-muted-foreground">{ev.date}</span>
      </div>
      <div className="font-bold">{ev.title}</div>
      <div className="text-xs text-muted-foreground mt-1">{ev.location}</div>
      <Button size="sm" className="mt-3 w-full bg-brand-blue text-white hover:bg-brand-blue/90" onClick={register}>
        Register
      </Button>
    </Card>
  );
}

function PayButton({ onPaid, label, user }) {
  const [busy, setBusy] = useState(false);

  const pay = async () => {
    setBusy(true);
    try {
      const { payWithRazorpay } = await import("@/lib/razorpay");
      await payWithRazorpay({
        amount: 1300,
        purpose: "membership",
        prefill: {
          name: user?.full_name || "",
          email: user?.email || "",
          contact: user?.phone || user?.mobile || "",
        },
      });
      toast.success("Payment successful! Awaiting admin review.");
      onPaid?.();
    } catch (e) {
      if (e?.message === "PAYMENT_CANCELLED") {
        toast("Payment cancelled");
      } else {
        toast.error(e?.response?.data?.detail || e?.message || "Payment failed");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      onClick={pay}
      disabled={busy}
      className="bg-brand-green text-white hover:bg-brand-green/90"
      data-testid="pay-fee-btn"
    >
      <CreditCard className="w-4 h-4 mr-1.5" />
      {busy ? "Opening…" : (label || "Pay Membership Fee — ₹1,300")}
    </Button>
  );
}

function HelpDeskPanel() {
  const [tickets, setTickets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "Software Issue", subject: "", description: "" });

  const load = () => api.get("/tickets/mine").then(r => setTickets(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tickets", form);
      toast.success("Ticket submitted");
      setOpen(false); setForm({ category: "Software Issue", subject: "", description: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><LifeBuoy className="w-5 h-5 text-brand-blue" /><div className="font-bold">My Tickets</div></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-brand-blue text-white hover:bg-brand-blue/90" data-testid="new-ticket-btn">New Ticket</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Raise a Ticket</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Website Issue", "Network Issue", "Software Issue", "Infrastructure Issue"].map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subject</Label>
                <Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} required />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} required />
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-brand-blue text-white">Submit</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <Card>
        <ul className="divide-y divide-border">
          {tickets.length === 0 && <li className="p-6 text-center text-sm text-muted-foreground">No tickets yet.</li>}
          {tickets.map(t => (
            <li key={t.id} className="p-4 flex items-start justify-between gap-4">
              <div>
                <div className="font-semibold">{t.subject}</div>
                <div className="text-xs text-muted-foreground">{t.category} • {new Date(t.created_at).toLocaleString()}</div>
                <div className="text-sm mt-1">{t.description}</div>
              </div>
              <Badge variant={t.status === "Resolved" ? "default" : "secondary"}>{t.status}</Badge>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

/* ---------- Circulars & Government Orders panel ---------- */
const CAT_FILTERS = [
  { value: "all", label: "All", icon: FileText },
  { value: "Government Order", label: "Government Orders", icon: Landmark },
  { value: "Association Circular", label: "Association Circulars", icon: Newspaper },
  { value: "Download", label: "Downloads", icon: Download },
];
const CAT_STYLES = {
  "Government Order": "bg-brand-blue/10 text-brand-blue border-brand-blue/30",
  "Association Circular": "bg-brand-orange/10 text-brand-orange border-brand-orange/30",
  "Download": "bg-emerald-500/10 text-emerald-700 border-emerald-200",
};

function CircularsPanel({ circulars, search, setSearch, cat, setCat }) {
  const q = search.trim().toLowerCase();
  const filtered = circulars.filter(c => {
    if (cat !== "all" && c.category !== cat) return false;
    if (!q) return true;
    return [c.title, c.summary, c.category].some(v => (v || "").toLowerCase().includes(q));
  });
  const counts = CAT_FILTERS.reduce((acc, f) => {
    acc[f.value] = f.value === "all" ? circulars.length : circulars.filter(c => c.category === f.value).length;
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="circulars-panel">
      <Card className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-lg bg-brand-blue/10 text-brand-blue flex items-center justify-center shrink-0">
            <Landmark className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-lg">Government Orders & Circulars</h3>
            <p className="text-sm text-muted-foreground">Official orders, association circulars and downloadable resources. Click a title to open the attachment or download it.</p>
          </div>
        </div>
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by title, summary or category…"
              className="pl-9"
              data-testid="circulars-search"
            />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2" role="tablist" aria-label="Filter by category">
          {CAT_FILTERS.map(f => {
            const Icon = f.icon;
            const isActive = cat === f.value;
            return (
              <button
                key={f.value}
                type="button"
                role="tab"
                onClick={() => setCat(f.value)}
                className={`px-3 py-1.5 rounded-full border text-xs font-medium transition inline-flex items-center gap-1.5 ${
                  isActive
                    ? "bg-brand-blue text-white border-brand-blue"
                    : "bg-white text-foreground border-border hover:bg-muted/50"
                }`}
                data-testid={`circulars-filter-${f.value.replace(/\s+/g, "_")}`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{f.label}</span>
                <span className={`ml-1 rounded-full px-1.5 py-0 text-[10px] ${isActive ? "bg-white/20" : "bg-muted"}`}>
                  {counts[f.value] || 0}
                </span>
              </button>
            );
          })}
        </div>
      </Card>

      {!filtered.length ? (
        <Card className="p-10 text-center" data-testid="circulars-empty">
          <FileText className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <h3 className="font-semibold">
            {circulars.length ? "No results match your filter" : "No circulars published yet"}
          </h3>
          {circulars.length ? (
            <p className="text-sm text-muted-foreground mt-1">Try a different keyword or clear the filter.</p>
          ) : null}
        </Card>
      ) : (
        <div className="grid gap-3">
          {filtered.map(c => (
            <Card key={c.id} className="p-4 hover:shadow-md transition" data-testid={`circular-card-${c.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`inline-block text-[10px] font-semibold rounded-full px-2 py-0.5 border ${CAT_STYLES[c.category] || "bg-slate-500/10 text-slate-700 border-slate-200"}`}>
                      {c.category}
                    </span>
                    <span className="text-xs text-muted-foreground">{c.date}</span>
                  </div>
                  <h4 className="font-semibold text-base break-words">{c.title}</h4>
                  {c.summary && <p className="text-sm text-muted-foreground mt-1 break-words">{c.summary}</p>}
                </div>
                {c.file_url ? (
                  <div className="flex flex-wrap gap-2">
                    <a href={c.file_url} target="_blank" rel="noreferrer noopener" data-testid={`circular-open-${c.id}`}>
                      <Button variant="outline" size="sm">
                        <ExternalLink className="w-4 h-4 mr-1.5" /> Open
                      </Button>
                    </a>
                    <a href={c.file_url} download data-testid={`circular-download-${c.id}`}>
                      <Button variant="outline" size="sm">
                        <FileDown className="w-4 h-4 mr-1.5" /> Download
                      </Button>
                    </a>
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground italic">No attachment</span>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

